import { useEffect, useState } from 'react';
import { useClientUpdateApi } from '@hero/api-hooks';
import type { Client, ClientSettings, ItemPacking, ItemType, VehicleTypes } from '@hero/schema';
import { resolveLoadPackingAndQuantityFromVehicles } from '@hero/core';

/**
 * `pod_settings` value space — mirrors
 * `orderPODCopySettingSchema` (`'pod_image' | 'return_original_pod'`).
 * Kept inline as a string union rather than importing the schema
 * value at runtime so this hook stays type-only on `@hero/schema`.
 */
export type PodCopySetting = 'pod_image' | 'return_original_pod';

export interface SettingsDraft {
  item_default_name: string;
  item_default_type: ItemType | undefined;
  item_default_cost: number | undefined;
  item_default_packing: ItemPacking;
  item_default_quantity: number;
  item_default_weight: string;
  vehicle_preferred_types: VehicleTypes[];
  /**
   * Per-client "document copy returns" preference. Drives BOTH
   * `Order.settings.podCopy` (paper POD return) AND
   * `Invoice.originalCopySettings` (paper original invoice
   * return) for new docs created for this client. `undefined`
   * means "inherit the org default".
   */
  pod_settings: PodCopySetting | undefined;
}

function clientToDraft(client?: Client): SettingsDraft {
  const s = client?.settings;
  // `item_default_weight` is a `Weight` object (`{ value, type }`).
  // The modal expects a string for the numeric `<Input type="number">`,
  // so unwrap `value` here. Type/unit is preserved on save by reading
  // back from the persisted doc (defaulting to `'ton'` for new entries
  // — the modal label is "(tons)").
  const weightValue = s?.item_default_weight?.value;
  const vehicles = s?.vehicle_preferred_types ?? [];
  const derived = resolveLoadPackingAndQuantityFromVehicles(vehicles);
  return {
    item_default_name: s?.item_default_name ?? '',
    item_default_type: s?.item_default_type,
    item_default_cost: s?.item_default_cost,
    item_default_packing: s?.item_default_packing ?? derived.packing,
    item_default_quantity: s?.item_default_quantity ?? derived.quantity,
    item_default_weight:
      weightValue !== undefined && weightValue !== null
        ? String(weightValue)
        : '',
    vehicle_preferred_types: vehicles,
    pod_settings: (s?.pod_settings as PodCopySetting | undefined) ?? undefined,
  };
}

/**
 * Manager hook for the per-section "Edit order defaults" modal. Edits
 * the basic scalar / vehicle fields of `Client.settings`; the rest of
 * the sub-doc (`pod_settings`, recipient contacts, etc.) is preserved
 * across the patch.
 */
export function useEditClientSettingsManager(client: Client | undefined) {
  const updateClient = useClientUpdateApi();
  const [draft, setDraft] = useState<SettingsDraft>(() => clientToDraft(client));

  useEffect(() => {
    setDraft(clientToDraft(client));
  }, [client?._id, client?.settings]);

  const setField = <K extends keyof SettingsDraft>(field: K, value: SettingsDraft[K]) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  };

  const reset = () => {
    setDraft(clientToDraft(client));
    // Clear the mutation's `isSuccess` so re-opening the modal after a
    // successful save doesn't immediately auto-close (ModalForm watches
    // `isSuccess` to drive its 300ms close animation).
    updateClient.reset();
  };

  const applyVehicleDerivedLoadDefaults = (vehicleTypes: VehicleTypes[]) => {
    const derived = resolveLoadPackingAndQuantityFromVehicles(vehicleTypes);
    return {
      vehicle_preferred_types: vehicleTypes,
      item_default_packing: derived.packing,
      item_default_quantity: derived.quantity,
    };
  };

  const toggleVehicleType = (vt: VehicleTypes) => {
    setDraft(prev => {
      const set = new Set(prev.vehicle_preferred_types);
      if (set.has(vt)) set.delete(vt);
      else set.add(vt);
      const nextTypes = Array.from(set);
      return { ...prev, ...applyVehicleDerivedLoadDefaults(nextTypes) };
    });
  };

  /**
   * Replace the whole vehicle-types array — used by the multi-select
   * picker which already owns toggle logic internally.
   */
  const setVehicleTypes = (next: VehicleTypes[]) => {
    setDraft(prev => ({ ...prev, ...applyVehicleDerivedLoadDefaults(next) }));
  };

  const onSave = async () => {
    if (!client) return;
    // Merge over existing settings so unrelated fields (pod_settings,
    // contact arrays, default_type, etc.) survive the patch.
    const next: ClientSettings = {
      ...(client.settings ?? {}),
    };
    next.item_default_name = draft.item_default_name.trim() || undefined;
    next.item_default_type = draft.item_default_type;
    next.item_default_cost =
      typeof draft.item_default_cost === 'number' && draft.item_default_cost > 0
        ? draft.item_default_cost
        : undefined;
    // `item_default_weight` is `weightSchema` ({ value, type }) — not a
    // plain number — so wrap the parsed input in `{ value, type: 'ton' }`.
    // The modal labels the field as "(tons)" so `'ton'` is the right
    // unit; a stored entry with a different unit is preserved by
    // reading from `client.settings.item_default_weight.type` first.
    const w = parseFloat(draft.item_default_weight);
    if (Number.isFinite(w) && w > 0) {
      next.item_default_weight = {
        value: w,
        type: client.settings?.item_default_weight?.type ?? 'ton',
      };
    } else {
      next.item_default_weight = undefined;
    }
    next.vehicle_preferred_types = draft.vehicle_preferred_types.length
      ? draft.vehicle_preferred_types
      : undefined;
    next.item_default_packing = draft.item_default_packing;
    next.item_default_quantity = draft.item_default_quantity;
    // Per-client document-copy preference. `undefined` here means
    // "inherit the org default" — the backend resolution order in
    // `order.dto.service.settings()` is
    // `Client.settings.pod_settings → Org.settings.pod_settings →
    // ['pod_image']`.
    next.pod_settings = draft.pod_settings;

    await updateClient.mutateAsync({ _id: client._id, settings: next });
  };

  return {
    draft,
    setField,
    toggleVehicleType,
    setVehicleTypes,
    reset,
    onSave,
    isPending: updateClient.isPending,
    isSuccess: updateClient.isSuccess,
  };
}
